import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import React, { lazy, Suspense } from "react";
import retryComponent from "utils/retryComponent";

import AccessControl from "../components/common/AccessControl/accessControl";

//Lazy components
const Dashboard = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-dashboard" */ "../views/Dashboard/container")),
);
const DataConsumed = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-dataConsumed" */ "../views/DataConsumed/container")),
);
const Login = lazy(() => retryComponent(() => import(/* webpackChunkName: "v1-login" */ "../views/Login/container")));
const OpsLogin = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-opsLogin" */ "../views/OpsLogin/container")),
);
const ForgotPassword = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-forgotPassword" */ "../views/ForgotPassword/container")),
);
const Signup = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-signup" */ "../views/Signup/container")),
);
const ResetPassword = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-resetPassword" */ "../views/ResetPassword/container")),
);
const VideoGrid = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-videoGrid" */ "../views/VideoGrid/container")),
);
const UserManagement = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-userManagement" */ "../views/UserManagement")),
);
const AddNewVideoUploadingForm = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-addNewVideoUploadingForm" */ "../views/AddNewVideoUploadingForm/container"),
  ),
);
const EditVideoFormContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-editVideoFormContainer" */ "../views/EditVideoForm/container")),
);
const EditSummaryContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-editSummaryContainer" */ "../views/EditSummary/container")),
);
const ThirdPartyContainer = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-thirdPartyContainer" */ "../views/ThirdPartyMediaShare/container"),
  ),
);
const ConnectedApps = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-connectedApps" */ "../views/ConnectedApps/container")),
);
const ArchiveClipsComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-archiveClipsComponent" */ "../views/ArchiveClips/container")),
);
const ConfigurationComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-configurationComponent" */ "../views/Configuration/container")),
);
const UserProfileComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-userProfileComponent" */ "../views/UserProfile/container")),
);
const UserProfileBasicInfoEditComponent = lazy(() =>
  retryComponent(
    () =>
      import(
        /* webpackChunkName: "v1-userProfileBasicInfoEditComponent" */ "../views/UserProfileBasicInfoEdit/container"
      ),
  ),
);
const UserProfileUpdatePasswordEditComponent = lazy(() =>
  retryComponent(
    () =>
      import(
        /* webpackChunkName: "v1-userProfileUpdatePasswordEditComponent" */ "../views/UserProfileUpdatePasswordEdit/container"
      ),
  ),
);
const ReportsComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-reportsComponent" */ "../views/Reports/container")),
);
const UpdateStorageComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-updateStorageComponent" */ "../views/UpdateStorage/container")),
);
const GroupMappingComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-groupMappingComponent" */ "../views/GroupMapping/container")),
);
const PublishClipsComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-publishClipsComponent" */ "../views/PublishClips/container")),
);
const ArchiveStreamComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-archiveStreamComponent" */ "../views/ArchiveStreams/container")),
);
const VideosInfoNComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-videosInfoNComponent" */ "../views/VideosInfoN/container")),
);
const EditorPage = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-editorPage" */ "../views/EditorPage/container")),
);
const ArchiveSettingsComponent = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-archiveSettingsComponent" */ "../views/ArchiveSettings/container"),
  ),
);
const ClipsNewComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-clipsNewComponent" */ "../views/ClipsNew/container")),
);
const LiveEditComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-liveEditComponent" */ "../views/LiveEdit/container")),
);
const MyHighlightsComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-myHighlightsComponent" */ "../views/MyHighlights/container")),
);
const MyStoriesComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-myStoriesComponent" */ "../views/MyStories/container")),
);
const VideoPlayerContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-videoPlayerContainer" */ "../views/VideoPlayer/container")),
);
const GroupMappingDetailComponent = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-groupMappingDetailComponent" */ "../views/GroupMappingDetails/container"),
  ),
);
const SingleWebstoryEditorComponent = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-singleWebstoryEditorComponent" */ "../views/SingalWebstoryEditor/container"),
  ),
);
const ScoreBoardContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-scoreBoardContainer" */ "../views/ScoreBoard/container")),
);
const StreamDetailsContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-streamDetailsContainer" */ "../views/StreamDetails/container")),
);
const QuickHighlights = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-quickHighlights" */ "../views/QuickHighlights/container")),
);
const BulkEditorComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-bulkEditorComponent" */ "../views/BulkEdits/container")),
);
const QueueSettingContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-queueSettingContainer" */ "../views/QueueManagement/container")),
);
const ProcessContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-processContainer" */ "../views/ProcessTracking/container")),
);
const QueueProcessContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-queueProcessContainer" */ "../views/QueueProcess/container")),
);
const CropperContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-cropperContainer" */ "../views/Cropper/container")),
);
const AddNewVideoEditForm = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-addNewVideoEditForm" */ "../views/AddNewVideoEditForm/container"),
  ),
);
const SSOLogin = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-ssoLogin" */ "../views/SSO/container")),
);
const PublishHistory = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-publishHistory" */ "../views/PublishHistory/container")),
);
const OrganizationManagement = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-organizationManagement" */ "../views/OrganizationManagement")),
);
const WorkspaceManagement = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-workspaceManagement" */ "../views/WorkspaceManagement")),
);
const ClientProfile = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-clientProfile" */ "../views/ClientProfile/container")),
);
const InternalStatus = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-internalStatus" */ "../views/InternalStatus/container")),
);
const ClipRatingRule = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-clipRatingRule" */ "../views/ClipRatingRule/container")),
);
const PermissionManagement = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-permissionManagement" */ "../views/PermissionManagement/container"),
  ),
);
const TableComponent = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-tableComponent" */ "../views/UsersLists/container")),
);
const SubOrganizationComponent = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "v1-subOrganizationComponent" */ "../views/Sub_Orgnization/Container"),
  ),
);
const Overview = lazy(() => retryComponent(() => import(/* webpackChunkName: "v1-overview" */ "../views/Overview")));
const CopyStreamContainer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "v1-copyStreamContainer" */ "../views/CopyStream/container")),
);

const antIcon = <LoadingOutlined style={{ fontSize: 24, align: "center" }} spin />;
const Loader = () => {
  return (
    <Spin
      indicator={antIcon}
      className="loader__full"
      style={{
        position: "fixed",
        top: "0",
        right: "0",
        bottom: "0",
        left: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        zIndex: "99",
      }}
    />
  );
};

export const AsyncDashboard = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Dashboard />
    </Suspense>
  );
};

export const AsyncDataConsumed = () => {
  return (
    <Suspense fallback={<Loader />}>
      <DataConsumed />
    </Suspense>
  );
};

export const AsyncLogin = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Login />
    </Suspense>
  );
};

export const AsyncOpsLogin = () => {
  return (
    <Suspense fallback={<Loader />}>
      <OpsLogin />
    </Suspense>
  );
};

export const AsyncForgotPassword = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ForgotPassword />
    </Suspense>
  );
};

export const AsyncSignup = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Signup />
    </Suspense>
  );
};
//----------------------
export const AsyncResetPassword = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ResetPassword />
    </Suspense>
  );
};

export const AsyncVideoGrid = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="videos">
        <VideoGrid />
      </AccessControl>
    </Suspense>
  );
};

// export const AsyncClips = () => {
//   return (
//     <Suspense fallback={<Loader />}>
//       <Clips />
//     </Suspense>
//   );
// };

export const AsyncAddNewVideoUploadingForm = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="add-new-stream" page="add-new-stream">
        <AddNewVideoUploadingForm />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncEditVideoFormContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <EditVideoFormContainer />
    </Suspense>
  );
};

export const AsyncEditSummaryContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="edit-clip">
        <EditSummaryContainer />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncArchiveClipsComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="archive-entities">
        <ArchiveClipsComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncCopyStreamContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="copy-stream">
        <CopyStreamContainer />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncVideoPlayerContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <VideoPlayerContainer />
    </Suspense>
  );
};

export const AsyncThirdPartyContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="social-media-share" page="social-media-share">
        <ThirdPartyContainer />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncConnectedApps = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ConnectedApps />
    </Suspense>
  );
};

export const AsyncConfigurationComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="configuration" page="configuration">
        <ConfigurationComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncUserProfileComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="profile">
        <UserProfileComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncReportsComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="reports" page="reports">
        <ReportsComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncUserProfileBasicInfoEditComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <UserProfileBasicInfoEditComponent />
    </Suspense>
  );
};

export const AsyncUserProfileUpdatePasswordEditComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <UserProfileUpdatePasswordEditComponent />
    </Suspense>
  );
};

export const AsyncUpdateStorageComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <UpdateStorageComponent />
    </Suspense>
  );
};

export const AsyncGroupMappingComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <GroupMappingComponent />
    </Suspense>
  );
};

export const AsyncMyHighlightsComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="highlights" page="highlights">
        <MyHighlightsComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncMyStoriesComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="stories" page="stories">
        <MyStoriesComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncPublishClipsComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <PublishClipsComponent />
    </Suspense>
  );
};

export const AsyncArchiveStreamComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="archive" page="archive">
        <ArchiveStreamComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncVideosInfoNComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="clipInfo" page="clipInfo">
        <VideosInfoNComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncEditorPage = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="editor-page" page="editor-page">
        <EditorPage />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncArchiveSettingsComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="client-archive" page="client-archive">
        <ArchiveSettingsComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncClipsNewComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="live-tool" page="live-tool">
        <ClipsNewComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncLiveEditComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <LiveEditComponent />
    </Suspense>
  );
};

export const AsyncGroupMappingDetailComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <GroupMappingDetailComponent />
    </Suspense>
  );
};

export const AsyncSingleWebstoryEditorComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="single-webstory-editor" page="single-webstory-editor">
        <SingleWebstoryEditorComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncScoreBoardContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="scoreboard">
        <ScoreBoardContainer />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncStreamDetailsContainer = () => {
  return (
    <Suspense fallback={<Loader />}>
      <StreamDetailsContainer />
    </Suspense>
  );
};

export const AsyncQuickHighlights = () => {
  return (
    <Suspense fallback={<Loader />}>
      <QuickHighlights />
    </Suspense>
  );
};

export const AsyncBulkEditorComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="bulk-edits" page="bulk-edits">
        <BulkEditorComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncQueueSettingComponent = () => {
  return (
    <AccessControl code="accessible" name="queue-setting" page="queue-setting">
      <Suspense fallback={<Loader />}>
        <QueueSettingContainer />
      </Suspense>
    </AccessControl>
  );
};

export const AsyncProcessTrackingComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ProcessContainer />
    </Suspense>
  );
};

export const AsyncQueueProcessesComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <QueueProcessContainer />
    </Suspense>
  );
};

export const AsyncCropperComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <CropperContainer />
    </Suspense>
  );
};
export const AsyncAddNewVideoEditForm = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AddNewVideoEditForm />
    </Suspense>
  );
};
export const AsyncSSOLogin = () => {
  return (
    <Suspense fallback={<Loader />}>
      <SSOLogin />
    </Suspense>
  );
};
export const AsyncPublishHistory = () => {
  return (
    <Suspense fallback={<Loader />}>
      <PublishHistory />
    </Suspense>
  );
};

export const AsyncOrganizationManagement = () => {
  return (
    <Suspense fallback={<Loader />}>
      <OrganizationManagement />
    </Suspense>
  );
};

export const AsyncWorkspaceManagement = () => {
  return (
    <Suspense fallback={<Loader />}>
      <WorkspaceManagement />
    </Suspense>
  );
};

export const AsyncUserManagement = ({ opsView }) => {
  return (
    <Suspense fallback={<Loader />}>
      <UserManagement opsView={opsView} />
    </Suspense>
  );
};

export const AsyncClientProfile = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ClientProfile />
    </Suspense>
  );
};

export const AsyncInternalStatus = () => {
  return (
    <Suspense fallback={<Loader />}>
      <InternalStatus />
    </Suspense>
  );
};

export const AsyncClipRatingRule = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ClipRatingRule />
    </Suspense>
  );
};

export const AsyncTableComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <TableComponent />
    </Suspense>
  );
};
export const AsyncSubOrgnizationComponent = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" name="workspace" page="workspace">
        <SubOrganizationComponent />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncPermissionManagement = () => {
  return (
    <Suspense fallback={<Loader />}>
      <AccessControl code="accessible" page="permission-management">
        <PermissionManagement />
      </AccessControl>
    </Suspense>
  );
};

export const AsyncOverview = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Overview />
    </Suspense>
  );
};
